import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import RTTable from '../common/Table';
import RTFilters from '../common/RTFilters';
import RTFilteredTable from '../common/RTFilteredTable';
import tenantStorage from '../../utils/tenantStorage';
import RTButton from '../common/RTButton';
import { apiGet } from '../../api/apiClient';
import { toastError } from '../common/toast';
import '../common/rtCommon.css';
import './ViewPGList.css';
import RTPageLayout from '../common/RTPageLayout';

const ViewPGList = () => {
  const [loading, setLoading] = useState(false);
  const [pgList, setPgList] = useState([]);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    status: 'Active',
    name: '',
    mobile: '',
  });
  const [appliedFilters, setAppliedFilters] = useState({
    status: 'Active',
    name: '',
    mobile: '',
  });
  const navigate = useNavigate();
  const location = useLocation();

  const filterFields = useMemo(
    () => [
      {
        key: 'name',
        label: 'PG Name',
        type: 'text',
        width: 'medium',
        placeholder: 'Search by name...',
        applyImmediately: false,
      },
      {
        key: 'mobile',
        label: 'Mobile',
        type: 'text',
        width: 'medium',
        placeholder: 'Search mobile...',
        applyImmediately: false,
      },
      {
        key: 'status',
        label: 'Status',
        type: 'dropdown',
        width: 'compact',
        options: [
          { value: '', label: 'All' },
          { value: 'Active', label: 'Active' },
          { value: 'InActive', label: 'Inactive' },
        ],
        applyImmediately: false,
      },
    ],
    [],
  );

  const fetchPGList = async (filterParams = appliedFilters) => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (filterParams.status) params.status = filterParams.status;
      if (filterParams.mobile?.trim())
        params.mobile = filterParams.mobile.trim();
      if (filterParams.name?.trim()) params.name = filterParams.name.trim();

      const token = tenantStorage.getItem('token');
      const response = await apiGet('/paying-guest', {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setPgList(response.data || []);
      if (!response.data || response.data.length === 0) {
        toastError('No paying guests found for the selected filter.');
      }
    } catch (err) {
      setError('Failed to fetch paying guest list');
      toastError('Failed to fetch paying guest list');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPGList({ status: 'Active', name: '', mobile: '' });
  }, []);

  const handleFilterApply = () => {
    setAppliedFilters(filters);
    fetchPGList(filters);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value ?? '',
    }));
  };

  const handleClearFilters = () => {
    const defaultFilters = { status: 'Active', name: '', mobile: '' };
    setFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
    fetchPGList(defaultFilters);
  };

  const appliedFilterPills = useMemo(() => {
    const pills = [];
    if (appliedFilters.name?.trim()) {
      pills.push({
        key: 'name',
        label: `PG Name: ${appliedFilters.name.trim()}`,
      });
    }
    if (appliedFilters.mobile?.trim()) {
      pills.push({
        key: 'mobile',
        label: `Mobile: ${appliedFilters.mobile.trim()}`,
      });
    }
    if (appliedFilters.status) {
      pills.push({ key: 'status', label: `Status: ${appliedFilters.status}` });
    }
    return pills;
  }, [appliedFilters]);

  const handleRemoveAppliedFilter = (key) => {
    const next = { ...appliedFilters };
    if (key === 'status') {
      next.status = '';
    } else {
      next[key] = '';
    }
    setFilters(next);
    setAppliedFilters(next);
    fetchPGList(next);
  };

  const getTableTitle = () => {
    let title = 'Paying Guests';

    if (appliedFilters.status) {
      title += ` - ${appliedFilters.status}`;
    }

    if (appliedFilters.name?.trim()) {
      title += ` (Name: "${appliedFilters.name.trim()}")`;
    }

    if (appliedFilters.mobile?.trim()) {
      title += ` (Mobile: "${appliedFilters.mobile.trim()}")`;
    }

    return title;
  };

  const formatAllocationStatus = (status) => {
    if (!status) return 'N/A';
    switch (status) {
      case 'FutureAllocation':
        return 'Future Allocation';
      case 'Allocated':
        return 'Allocated';
      case 'AllocationEnded':
        return 'Allocation Ended';
      default:
        return status;
    }
  };

  const columns = [
    { Header: 'Name', accessor: 'name' },
    { Header: 'Mobile', accessor: 'mobile' },
    { Header: 'Status', accessor: 'status' },
    {
      Header: 'Allocated Building',
      accessor: 'building_name',
      Cell: ({ value }) => value || 'N/A',
    },
    {
      Header: 'Room Details',
      accessor: 'room_details',
      Cell: ({ value }) => value || 'N/A',
    },
    {
      Header: 'Allocation Status',
      accessor: 'allocation_status',
      Cell: ({ value }) => (
        <span
          className={`allocation-status ${value ? value.toLowerCase().replace('allocation', '') : 'none'}`}
        >
          {formatAllocationStatus(value)}
        </span>
      ),
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      Cell: ({ row }) => (
        <RTButton
          type="link"
          onClick={() => navigate(`/paying-guest/view/${row.pg_id}`)}
          style={{ padding: '4px 8px', fontSize: '0.9rem' }}
        >
          View
        </RTButton>
      ),
    },
  ];

  return (
    <RTPageLayout
      activePath={location.pathname}
      title="Paying Guest List"
      actions={
        <>
          <RTButton variant="secondary" onClick={() => navigate(-1)}>
            &#8592; Back
          </RTButton>
        </>
      }
      mainSection={true}
    >
      {error && <div className="alert alert-danger">{error}</div>}
      <RTFilteredTable
        loading={loading}
        loadingMessage="Loading paying guests..."
        filters={
          <RTFilters
            fields={filterFields}
            values={filters}
            onChange={handleFilterChange}
            showApplyButton={true}
            onApply={handleFilterApply}
            pills={appliedFilterPills}
            onRemovePill={handleRemoveAppliedFilter}
            onClearAll={handleClearFilters}
          />
        }
      >
        <RTTable
          columns={columns}
          data={pgList}
          title={getTableTitle()}
          showPagination={true}
          showSearch={true}
          exportable={true}
          exportFileName="paying_guests"
          noDataText="No paying guests found."
        />
      </RTFilteredTable>
    </RTPageLayout>
  );
};

export default ViewPGList;
